// src/pages/UserMeet/JoinUserMeetingPage.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Video,
  VideoOff,
  Mic,
  MicOff,
  Users,
  RefreshCw,
  LogIn,
  Copy,
  Check,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { getMeetingDetails, joinMeeting } from "@/Services/UserMeetServices";
import WaitingRoomScreen from "@/components/UserMeet/User-Meet-Private/WaitingRoomScreen";

// ---------------------------------------------------------------------------
// CONSTANTS
// ---------------------------------------------------------------------------

const ROOM_META = {
  4: { label: "Huddle", icon: "👥" },
  6: { label: "Team Room", icon: "🤝" },
  10: { label: "Conference", icon: "🏛️" },
  50: { label: "Town Hall", icon: "🎤" },
};

const DEVICE_ERRORS = {
  MIC_DENIED: {
    message:
      "Microphone access denied. Click the [i] icon in your browser's address bar to allow access.",
  },
  CAMERA_DENIED: {
    message:
      "Camera access denied. Click the [i] icon in your browser's address bar to allow access.",
  },
  MIC_IN_USE: {
    message:
      "Microphone is being used by another application. Please close other apps and try again.",
  },
  CAMERA_IN_USE: {
    message:
      "Camera is being used by another application. Please close other apps and try again.",
  },
  MIC_NOT_FOUND: {
    message: "No microphone found. Please connect a microphone and try again.",
  },
  CAMERA_NOT_FOUND: {
    message: "No camera found. Please connect a camera and try again.",
  },
  GENERIC_ERROR: {
    message:
      "Could not access camera/microphone. Please check browser permissions.",
  },
};

// ---------------------------------------------------------------------------
// PURE UTILITIES
// ---------------------------------------------------------------------------

const getName = (u) =>
  u?.name ??
  u?.fullName ??
  u?.userName ??
  u?.username ??
  u?.email ??
  "Participant";

const getAvatarUrl = (u) =>
  u?.avatar ?? u?.profileImage ?? u?.profilePic ?? u?.image ?? null;

const getInitials = (name) => {
  const s = String(name || "").trim();
  if (!s) return "NA";
  const parts = s.split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] || "";
  const b = parts.length > 1 ? parts[1]?.[0] || "" : parts[0]?.[1] || "";
  return (a + b).toUpperCase();
};

const getSeatLabel = (index) => String(index + 1);

const showDeviceError = (e, type) => {
  const map = {
    NotAllowedError: type === "mic" ? "MIC_DENIED" : "CAMERA_DENIED",
    PermissionDeniedError: type === "mic" ? "MIC_DENIED" : "CAMERA_DENIED",
    NotReadableError: type === "mic" ? "MIC_IN_USE" : "CAMERA_IN_USE",
    NotFoundError: type === "mic" ? "MIC_NOT_FOUND" : "CAMERA_NOT_FOUND",
  };
  const key = map[e.name] || "GENERIC_ERROR";
  toast.error(DEVICE_ERRORS[key].message, { duration: 5000 });
};

// ---------------------------------------------------------------------------
// RECTANGULAR TABLE SEAT LAYOUT
// ---------------------------------------------------------------------------

function rectangularSeatPositions(count) {
  if (count <= 4) {
    return [
      { x: 0, y: 50, side: "left" },
      { x: 50, y: 0, side: "top" },
      { x: 100, y: 50, side: "right" },
      { x: 50, y: 100, side: "bottom" },
    ];
  }

  if (count <= 6) {
    return [
      { x: 0, y: 50, side: "left" },
      { x: 33, y: 0, side: "top" },
      { x: 66, y: 0, side: "top" },
      { x: 100, y: 50, side: "right" },
      { x: 66, y: 100, side: "bottom" },
      { x: 33, y: 100, side: "bottom" },
    ];
  }

  if (count <= 10) {
    return [
      { x: 0, y: 50, side: "left" },
      { x: 20, y: 0, side: "top" },
      { x: 40, y: 0, side: "top" },
      { x: 60, y: 0, side: "top" },
      { x: 80, y: 0, side: "top" },
      { x: 100, y: 50, side: "right" },
      { x: 80, y: 100, side: "bottom" },
      { x: 60, y: 100, side: "bottom" },
      { x: 40, y: 100, side: "bottom" },
      { x: 20, y: 100, side: "bottom" },
    ];
  }

  const positions = [];
  const rows = Math.ceil(count / 10);
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / 10);
    const col = i % 10;
    const totalInRow = Math.min(10, count - row * 10);
    const xOffset = (10 - totalInRow) / 2;
    positions.push({
      x: ((col + xOffset) / 9) * 100,
      y: (row / Math.max(1, rows - 1)) * 100,
      side: "grid",
    });
  }
  return positions;
}

// ---------------------------------------------------------------------------
// BOARDROOM TABLE
// ---------------------------------------------------------------------------

const BoardroomTable = memo(({ seats, capacity, meetingTitle }) => {
  const positions = useMemo(
    () => rectangularSeatPositions(capacity),
    [capacity],
  );

  const isLargeRoom = capacity > 10;

  if (isLargeRoom) {
    return (
      <TownHallGrid
        seats={seats}
        capacity={capacity}
        meetingTitle={meetingTitle}
      />
    );
  }

  const tableW = capacity <= 4 ? 160 : capacity <= 6 ? 200 : 280;
  const tableH = capacity <= 4 ? 90 : 100;
  const containerW = tableW + 120;
  const containerH = tableH + 140;

  return (
    <div
      className="relative mx-auto"
      style={{ width: containerW, height: containerH, perspective: "800px" }}
      aria-label="Boardroom table seating diagram"
      role="img"
    >
      <div
        className="absolute"
        style={{
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%) rotateX(35deg)",
          transformStyle: "preserve-3d",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(-10px)",
            width: tableW + 16,
            height: tableH + 16,
            borderRadius: 18,
            background: "rgba(0,0,0,0.45)",
            filter: "blur(18px)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: tableW,
            height: tableH,
            borderRadius: 14,
            background:
              "linear-gradient(135deg, #E7D9C0 0%, #C6A87C 30%, #8A6F4A 70%, #5A4530 100%)",
            boxShadow:
              "inset 0 0 0 2px rgba(60,40,20,0.45), inset 0 -5px 16px rgba(0,0,0,0.35), 0 6px 14px rgba(0,0,0,0.5)",
            border: "1px solid rgba(0,0,0,0.4)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(0.5px)",
            width: tableW - 20,
            height: tableH - 20,
            borderRadius: 10,
            border: "1px solid rgba(60,40,20,0.3)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%) translateZ(1px)",
            width: Math.min(100, tableW - 40),
            height: tableH - 36,
            borderRadius: 8,
            background: "rgba(20,12,4,0.4)",
            border: "1.5px solid rgba(198,168,124,0.3)",
            boxShadow: "inset 0 -2px 6px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "6px",
          }}
        >
          <span
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 10,
              color: "rgba(198,168,124,0.85)",
              textAlign: "center",
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {meetingTitle || "Meeting"}
          </span>
        </div>

        {positions.map((p, i) => {
          const chairRotation =
            p.side === "top"
              ? 180
              : p.side === "bottom"
                ? 0
                : p.side === "left"
                  ? 90
                  : -90;

          const px = (p.x / 100) * tableW - tableW / 2;
          const py = (p.y / 100) * tableH - tableH / 2;

          const outset =
            p.side === "top"
              ? -20
              : p.side === "bottom"
                ? 20
                : p.side === "left"
                  ? -20
                  : 20;
          const offsetX = p.side === "left" || p.side === "right" ? outset : 0;
          const offsetY = p.side === "top" || p.side === "bottom" ? outset : 0;

          return (
            <div
              key={i}
              className="absolute"
              style={{
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) translate(${px + offsetX}px, ${py + offsetY}px) translateZ(4px) rotate(${chairRotation}deg)`,
              }}
            >
              <div
                style={{
                  width: 22,
                  height: 16,
                  borderRadius: "5px 5px 3px 3px",
                  background:
                    "linear-gradient(180deg, #2a3a36 0%, #1a2522 100%)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  boxShadow:
                    "0 2px 4px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.05)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: -2,
                  transform: "translateX(-50%) rotateX(-45deg)",
                  transformOrigin: "bottom center",
                  width: 18,
                  height: 11,
                  borderRadius: "3px 3px 2px 2px",
                  background:
                    "linear-gradient(180deg, #3a4a45 0%, #1f2c28 100%)",
                  border: "1px solid rgba(255,255,255,0.07)",
                }}
              />
            </div>
          );
        })}
      </div>

      <div className="absolute inset-0">
        {positions.map((p, i) => {
          const occupant = seats[i] ?? null;
          const label = getSeatLabel(i);
          const name = occupant ? getName(occupant) : "";
          const avatarUrl = occupant ? getAvatarUrl(occupant) : null;
          const initials = name ? getInitials(name) : label;
          const isEmpty = !occupant;

          const padX = 60;
          const padTop = 30;
          const padBot = 40;
          const availW = containerW - padX * 2;
          const availH = containerH - padTop - padBot;

          let ax = padX + (p.x / 100) * availW;
          let ay = padTop + (p.y / 100) * availH;

          const push = 28;
          if (p.side === "top") ay -= push;
          if (p.side === "bottom") ay += push;
          if (p.side === "left") ax -= push;
          if (p.side === "right") ax += push;

          return (
            <div
              key={i}
              className="absolute"
              style={{ left: ax - 16, top: ay - 16 }}
            >
              {isEmpty ? (
                <div
                  className="w-8 h-8 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center"
                  title={`Seat ${label} — open`}
                >
                  <span className="text-[9px] text-white/30 font-semibold">
                    {label}
                  </span>
                </div>
              ) : (
                <div
                  className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/30 shadow-lg"
                  style={{ boxShadow: "0 3px 10px -2px rgba(0,0,0,0.55)" }}
                  title={name}
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold bg-[#0A1D1A] text-[#C6A87C]">
                      {initials}
                    </span>
                  )}
                </div>
              )}
              <div className="flex justify-center mt-0.5">
                <span
                  className={`text-[8px] font-bold px-1 rounded ${
                    isEmpty ? "text-white/20" : "text-[#C6A87C]"
                  }`}
                >
                  {label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
BoardroomTable.displayName = "BoardroomTable";

// ---------------------------------------------------------------------------
// TOWN HALL GRID
// ---------------------------------------------------------------------------

const TownHallGrid = memo(({ seats, capacity, meetingTitle }) => {
  const displayCount = Math.min(capacity, 50);
  const cols = 10;
  const rows = Math.ceil(displayCount / cols);

  return (
    <div className="w-full" aria-label="Town hall seating grid" role="img">
      <div className="mx-auto mb-6 relative">
        <div
          className="mx-auto rounded-xl px-6 py-3 text-center"
          style={{
            maxWidth: 260,
            background:
              "linear-gradient(135deg, #C6A87C 0%, #8A6F4A 60%, #5A4530 100%)",
            boxShadow:
              "inset 0 0 0 1.5px rgba(60,40,20,0.4), 0 4px 12px rgba(0,0,0,0.4)",
          }}
        >
          <span
            className="text-xs font-semibold tracking-wide"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              color: "rgba(6,20,18,0.85)",
            }}
          >
            {meetingTitle || "Meeting"}
          </span>
          <div
            className="text-[9px] mt-0.5 opacity-60"
            style={{ color: "#061412" }}
          >
            Stage
          </div>
        </div>
        <div
          className="mx-auto mt-1 rounded-full"
          style={{
            width: 180,
            height: 6,
            background: "rgba(0,0,0,0.3)",
            filter: "blur(6px)",
          }}
        />
      </div>

      <div className="space-y-1.5">
        {Array.from({ length: rows }).map((_, row) => {
          const seatsInRow = Math.min(cols, displayCount - row * cols);
          return (
            <div key={row} className="flex items-center justify-center gap-1.5">
              <span className="w-4 text-[8px] text-white/20 font-mono text-right shrink-0">
                {String.fromCharCode(65 + row)}
              </span>
              {Array.from({ length: seatsInRow }).map((_, col) => {
                const idx = row * cols + col;
                const occupant = seats[idx] ?? null;
                const isEmpty = !occupant;
                const name = occupant ? getName(occupant) : "";
                const avatarUrl = occupant ? getAvatarUrl(occupant) : null;
                const initials = name ? getInitials(name) : "";

                return (
                  <div
                    key={col}
                    className="relative group"
                    title={isEmpty ? `Seat ${getSeatLabel(idx)} — open` : name}
                  >
                    {isEmpty ? (
                      <div className="w-6 h-6 rounded border border-dashed border-white/10 flex items-center justify-center">
                        <span className="text-[7px] text-white/15 font-mono">
                          {col + 1}
                        </span>
                      </div>
                    ) : (
                      <div className="w-6 h-6 rounded overflow-hidden border border-white/25">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="w-full h-full flex items-center justify-center text-[7px] font-bold bg-[#0A1D1A] text-[#C6A87C]">
                            {initials}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
});
TownHallGrid.displayName = "TownHallGrid";

// ---------------------------------------------------------------------------
// PARTICIPANT ROW
// ---------------------------------------------------------------------------

const ParticipantRow = memo(({ occupant, index }) => {
  const name = getName(occupant);
  const avatarUrl = getAvatarUrl(occupant);
  const initials = getInitials(name);
  const label = index;

  return (
    <div className="flex items-center gap-2 rounded-xl border border-white/[0.05] bg-white/[0.02] px-3 py-2 hover:bg-white/[0.04] transition-colors">
      <span className="w-6 h-6 rounded-full bg-[#C6A87C]/15 border border-[#C6A87C]/30 text-[#C6A87C] text-[10px] font-bold flex items-center justify-center shrink-0">
        {label}
      </span>
      <div className="w-8 h-8 rounded-full overflow-hidden border border-white/20 shrink-0">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={name}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold bg-[#0A1D1A] text-[#C6A87C]">
            {initials}
          </span>
        )}
      </div>
      <span className="text-sm text-white/80 truncate flex-1">{name}</span>
      <span className="w-1.5 h-1.5 rounded-full bg-[#7BC8A6] shrink-0" />
    </div>
  );
});
ParticipantRow.displayName = "ParticipantRow";

const EmptySeatRow = memo(({ index }) => {
  const label = getSeatLabel(index);
  return (
    <div className="flex items-center gap-2 rounded-xl border border-dashed border-white/[0.06] bg-white/[0.02] px-3 py-2 opacity-50">
      <span className="w-6 h-6 rounded-full border border-dashed border-white/20 text-white/30 text-[10px] font-bold flex items-center justify-center shrink-0">
        {label}
      </span>
      <div className="w-8 h-8 rounded-full border-2 border-dashed border-white/15" />
      <span className="text-sm text-white/30 italic">Open seat</span>
    </div>
  );
});
EmptySeatRow.displayName = "EmptySeatRow";

// ---------------------------------------------------------------------------
// DEVICE CONTROL BUTTON
// ---------------------------------------------------------------------------

const DeviceButton = memo(
  ({
    active,
    onToggle,
    activeIcon: ActiveIcon,
    inactiveIcon: InactiveIcon,
    activeLabel,
    inactiveLabel,
  }) => (
    <button
      type="button"
      onClick={onToggle}
      className={`flex flex-col items-center gap-2 px-6 py-4 rounded-2xl border transition-all ${
        active
          ? "border-[#C6A87C]/40 bg-[#C6A87C]/10 text-[#C6A87C]"
          : "border-white/[0.08] bg-white/[0.03] text-white/40"
      } hover:border-white/30 hover:text-white`}
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.05]">
        {active ? (
          <ActiveIcon size={18} aria-hidden="true" />
        ) : (
          <InactiveIcon size={18} aria-hidden="true" />
        )}
      </div>
      <span className="text-xs font-medium">
        {active ? activeLabel : inactiveLabel}
      </span>
    </button>
  ),
);
DeviceButton.displayName = "DeviceButton";

// ---------------------------------------------------------------------------
// LOADING STATE
// ---------------------------------------------------------------------------

function LoadingState() {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      <div className="text-center">
        <div className="w-12 h-12 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/40">Loading meeting details…</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

export default function JoinUserMeetingPage() {
  const { joinCode } = useParams();
  const navigate = useNavigate();

  // ── Auth token (passed to WaitingRoomScreen so it can self-connect the socket) ──
  const authToken =
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("accessToken") ||
    null;

  // ── Meeting data ──
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinUiError, setJoinUiError] = useState("");
  const [copied, setCopied] = useState(false);

  // ✅ Waiting room state
  const [waitingRoom, setWaitingRoom] = useState(null);
  // Shape: { meetingId, participantId, message } | null

  // ── Camera / Mic refs ──
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const cameraTrackRef = useRef(null);
  const audioTrackRef = useRef(null);

  const [camOn, setCamOn] = useState(false);
  const [micOn, setMicOn] = useState(false);

  // ── Fetch meeting details on mount ──
  useEffect(() => {
    if (!joinCode) {
      navigate("/user-meet", { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        const res = await getMeetingDetails(joinCode);

        if (cancelled) return;

        if (!res?.success) {
          throw new Error(res?.message || "Meeting not found");
        }

        setMeeting(res.data);
      } catch (err) {
        if (cancelled) return;
        toast.error(err?.message || "Failed to load meeting details");
        navigate("/user-meet", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [joinCode, navigate]);

  // ── Page title ──
  useEffect(() => {
    document.title = meeting?.title
      ? `Join ${meeting.title} | UserMeet`
      : "Join Meeting | UserMeet";
  }, [meeting?.title]);

  // ── Init camera + mic — skip if in waiting room ──
  useEffect(() => {
    // ✅ Don't request devices if showing waiting room
    if (waitingRoom) return;

    let cancelled = false;

    const initDevices = async () => {
      let camTrack = null;
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        camTrack = s.getVideoTracks()[0] || null;
      } catch (e) {
        console.warn("Camera init failed:", e.name);
      }

      let micTrack = null;
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          video: false,
          audio: true,
        });
        micTrack = s.getAudioTracks()[0] || null;
      } catch (e) {
        console.warn("Mic init failed:", e.name);
      }

      if (cancelled) {
        camTrack?.stop();
        micTrack?.stop();
        return;
      }

      cameraTrackRef.current = camTrack;
      audioTrackRef.current = micTrack;

      const tracks = [camTrack, micTrack].filter(Boolean);
      if (tracks.length > 0) {
        const stream = new MediaStream(tracks);
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      }

      setCamOn(!!camTrack);
      setMicOn(!!micTrack);
    };

    initDevices();

    return () => {
      cancelled = true;
      cameraTrackRef.current?.stop();
      audioTrackRef.current?.stop();
      cameraTrackRef.current = null;
      audioTrackRef.current = null;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [waitingRoom]);

  // ── Stop stream helper ──
  const stopStream = useCallback(() => {
    cameraTrackRef.current?.stop();
    audioTrackRef.current?.stop();
    cameraTrackRef.current = null;
    audioTrackRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  // ── Toggle mic ──
  const toggleMic = useCallback(async () => {
    if (micOn) {
      if (audioTrackRef.current) {
        audioTrackRef.current.stop();
        streamRef.current?.removeTrack(audioTrackRef.current);
        audioTrackRef.current = null;
      }
      setMicOn(false);
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: false,
        audio: true,
      });
      const micTrack = s.getAudioTracks()[0];
      if (!micTrack) return;
      audioTrackRef.current = micTrack;
      if (streamRef.current) {
        streamRef.current.addTrack(micTrack);
      } else {
        streamRef.current = new MediaStream([micTrack]);
      }
      setMicOn(true);
    } catch (e) {
      console.error("Mic ON failed:", e.name);
      audioTrackRef.current = null;
      setMicOn(false);
      showDeviceError(e, "mic");
    }
  }, [micOn]);

  // ── Toggle camera ──
  const toggleCam = useCallback(async () => {
    if (camOn) {
      if (cameraTrackRef.current) {
        cameraTrackRef.current.stop();
        streamRef.current?.removeTrack(cameraTrackRef.current);
        cameraTrackRef.current = null;
      }
      setCamOn(false);
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });
      const camTrack = s.getVideoTracks()[0];
      if (!camTrack) return;
      cameraTrackRef.current = camTrack;
      if (streamRef.current) {
        streamRef.current.addTrack(camTrack);
      } else {
        streamRef.current = new MediaStream([camTrack]);
      }
      if (videoRef.current) {
        videoRef.current.srcObject = streamRef.current;
        await videoRef.current.play().catch(() => {});
      }
      setCamOn(true);
    } catch (e) {
      console.error("Camera ON failed:", e.name);
      cameraTrackRef.current = null;
      setCamOn(false);
      showDeviceError(e, "cam");
    }
  }, [camOn]);

  // ── Copy shareable link ──
  const copyLink = useCallback(() => {
    const link =
      meeting?.shareableLink ||
      `${window.location.origin}/user-meet/join/${joinCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  }, [meeting?.shareableLink, joinCode]);

  // ── Handle back ──
  const handleBack = useCallback(() => {
    stopStream();
    navigate("/user-meet");
  }, [stopStream, navigate]);

  // ✅ Handle join — now detects WAITING status
  const handleJoinNow = useCallback(async () => {
    setJoinUiError("");

    try {
      setJoining(true);

      const res = await joinMeeting(joinCode);

      // ✅ WAITING status — show waiting room screen
      if (res?.status === "WAITING") {
        stopStream();

        setWaitingRoom({
          meetingId: res.meetingId || meeting?.meetingId,
          participantId: res.participantId,
          message: res.message,
        });

        toast.info(
          res.message ||
            "Join request sent. Please wait for the host to admit you.",
          { duration: 5000 },
        );
        return;
      }

      if (!res?.success) {
        throw new Error(res?.message || "Failed to join meeting");
      }

      toast.success(res.message || "Joined meeting successfully!");

      stopStream();

      navigate(`/user-meet/room/${joinCode}`, {
        state: {
          meetingData: res.data,
          initialMicEnabled: micOn,
          initialCameraEnabled: camOn,
        },
      });
    } catch (err) {
      const status = err?.response?.status;
      const code = err?.response?.data?.code;
      const message = err?.response?.data?.message || err?.message;

      if (status === 409 && code === "MEETING_FULL") {
        setJoinUiError(
          `Meeting is full (${capacity}/${capacity} participants). Please try again later.`,
        );
      } else if (status === 400) {
        setJoinUiError("This meeting has already ended.");
      } else if (status === 404) {
        setJoinUiError("Meeting not found. Please check the join code.");
      } else {
        setJoinUiError(message || "Failed to join meeting. Please try again.");
      }
    } finally {
      setJoining(false);
    }
  }, [joinCode, micOn, camOn, navigate, stopStream, meeting]);

  // ✅ Handle admitted — navigate to room with livekit data
  const handleAdmitted = useCallback(
    (livekitData) => {
      const meetingData = {
        meetingId: livekitData.meetingId || waitingRoom?.meetingId,
        livekitToken: livekitData.livekitToken,
        livekitIdentity: livekitData.livekitIdentity,
        livekitServerUrl: livekitData.livekitServerUrl,
        livekitRoomName: livekitData.livekitRoomName,
        title: meeting?.title || "Meeting",
        joinCode: joinCode,
        capacity: meeting?.capacity || 8,
        isCreator: false,
      };

      navigate(`/user-meet/room/${joinCode}`, {
        state: {
          meetingData,
          initialMicEnabled: micOn,
          initialCameraEnabled: camOn,
        },
        replace: true,
      });
    },
    [waitingRoom, meeting, joinCode, micOn, camOn, navigate],
  );

  // ✅ Handle denied — go back to meetings list
  const handleDenied = useCallback(() => {
    setWaitingRoom(null);
    navigate("/user-meet", { replace: true });
  }, [navigate]);

  // ✅ Handle cancel — go back to join page UI
  const handleCancelWaiting = useCallback(() => {
    setWaitingRoom(null);
  }, []);

  // ── Derived values ──
  const capacity = meeting?.capacity || 10;
  const roomType = meeting?.roomType || String(capacity);
  const roomInfo = ROOM_META[roomType] || ROOM_META[10];

  const activeParticipants = useMemo(() => {
    if (!meeting?.participants) return [];
    return meeting.participants.filter((p) => p.participantStatus === "active");
  }, [meeting?.participants]);

  const seatSlots = useMemo(() => {
    const list = Array.from({ length: capacity }, () => null);
    for (let i = 0; i < Math.min(activeParticipants.length, capacity); i++) {
      list[i] = activeParticipants[i];
    }
    return list;
  }, [activeParticipants, capacity]);

  const filled = meeting?.currentOccupancy || 0;
  const seatsLeft = Math.max(0, capacity - filled);
  const isFull = filled >= capacity;
  const isActive = meeting?.isActive !== false;
  const canJoin = isActive && !isFull;

  const visibleSeats = useMemo(() => {
    if (capacity <= 10) return seatSlots;
    const occupied = seatSlots.filter(Boolean);
    const emptyToShow = Math.min(5, capacity - occupied.length);
    const result = [...occupied];
    for (let i = 0; i < emptyToShow; i++) {
      result.push(null);
    }
    return result;
  }, [seatSlots, capacity]);

  const infoText = useMemo(() => {
    if (micOn && camOn) return "You will join with mic and camera on";
    if (micOn && !camOn) return "You will join with mic on, camera off";
    if (!micOn && camOn) return "You will join with mic off, camera on";
    return "You will join with mic and camera off";
  }, [micOn, camOn]);

  // ---------------------------------------------------------------------------
  // RENDER GUARDS
  // ---------------------------------------------------------------------------

  if (loading) return <LoadingState />;

  if (!meeting) return null;

  // ✅ Waiting room — replaces entire page
  if (waitingRoom) {
    return (
      <WaitingRoomScreen
        joinCode={joinCode}
        meetingId={waitingRoom.meetingId}
        participantId={waitingRoom.participantId}
        meetingTitle={meeting?.title}
        message={waitingRoom.message}
        token={authToken}
        onAdmitted={handleAdmitted}
        onDenied={handleDenied}
        onCancel={handleCancelWaiting}
      />
    );
  }

  if (!isActive) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="glass rounded-2xl p-10 max-w-md w-full mx-4 text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-red-500/15 flex items-center justify-center mx-auto">
            <AlertCircle size={24} className="text-red-400" />
          </div>
          <h2 className="font-display text-2xl text-white">Meeting Ended</h2>
          <p className="text-white/55 text-sm">
            This meeting is no longer active.
          </p>
          <button
            onClick={() => navigate("/user-meet")}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/[0.12] text-white/70 hover:border-white/30 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back to Meetings
          </button>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // MAIN RENDER
  // ---------------------------------------------------------------------------

  return (
    <div
      className="min-h-screen"
      style={{
        background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
      }}
    >
      {/* ── HERO ── */}
      <section className="border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <button
            type="button"
            onClick={handleBack}
            className="mb-8 inline-flex items-center gap-2 text-xs text-white/55 hover:text-white/90 transition-colors"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Back to Meetings
          </button>

          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#2A7E5D]/15 border border-[#2A7E5D]/40">
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-[#7BC8A6] animate-pulse"
                    aria-hidden="true"
                  />
                  <span className="text-[10px] font-semibold tracking-widest uppercase text-[#7BC8A6]">
                    {isFull ? "Meeting Full" : "Ready to Join"}
                  </span>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#C6A87C]/10 border border-[#C6A87C]/30">
                  <span className="text-xs">{roomInfo.icon}</span>
                  <span className="text-[10px] font-semibold tracking-widest uppercase text-[#C6A87C]">
                    {roomInfo.label}
                  </span>
                </div>
              </div>

              <h1
                className="font-light text-5xl md:text-6xl tracking-tighter leading-[0.95] mb-4 text-white"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                {meeting.title || "Untitled Meeting"}
              </h1>

              {meeting.description && (
                <p className="text-white/50 text-sm mb-4 max-w-lg">
                  {meeting.description}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-4 text-sm text-white/55">
                <span className="flex items-center gap-2">
                  <Users
                    size={13}
                    className="text-[#C6A87C]"
                    aria-hidden="true"
                  />
                  {filled} of {capacity} seats filled ·{" "}
                  <span className={isFull ? "text-red-400" : "text-[#7BC8A6]"}>
                    {isFull ? "Full" : `${seatsLeft} open`}
                  </span>
                </span>
                <span className="text-white/30">·</span>
                <span className="font-mono text-[#C6A87C] text-xs">
                  {joinCode}
                </span>
                <span className="text-white/30">·</span>
                <span className="text-white/40 text-xs">
                  Host: {meeting.creator?.name || "Unknown"}
                </span>
              </div>
            </div>

            <div className="md:col-span-4 flex md:justify-end">
              <p className="text-xs text-white/40 italic">{infoText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* ── LEFT: Camera preview + controls ── */}
            <div className="space-y-6">
              <div
                className="rounded-2xl overflow-hidden border border-white/[0.06]"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="relative bg-black/40 aspect-video">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      transform: "scaleX(-1)",
                      display: camOn ? "block" : "none",
                    }}
                  />
                  {!camOn && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                      <div className="w-16 h-16 rounded-2xl bg-white/[0.05] border border-white/[0.08] flex items-center justify-center">
                        <VideoOff
                          size={28}
                          className="text-white/30"
                          aria-hidden="true"
                        />
                      </div>
                      <span className="text-white/30 text-sm">
                        Camera is off
                      </span>
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${
                        camOn
                          ? "bg-[#2A7E5D]/20 border-[#2A7E5D]/40 text-[#7BC8A6]"
                          : "bg-white/[0.04] border-white/[0.08] text-white/30"
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          camOn ? "bg-[#7BC8A6] animate-pulse" : "bg-white/20"
                        }`}
                      />
                      {camOn ? "Live preview" : "Camera off"}
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <p className="text-xs text-white/40 mb-4 font-medium uppercase tracking-widest">
                    Device controls
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <DeviceButton
                      active={micOn}
                      onToggle={toggleMic}
                      activeIcon={Mic}
                      inactiveIcon={MicOff}
                      activeLabel="Mute"
                      inactiveLabel="Unmute"
                    />
                    <DeviceButton
                      active={camOn}
                      onToggle={toggleCam}
                      activeIcon={Video}
                      inactiveIcon={VideoOff}
                      activeLabel="Stop Video"
                      inactiveLabel="Start Video"
                    />
                  </div>
                </div>
              </div>

              <div
                className="rounded-2xl p-5 border border-white/[0.06]"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#C6A87C]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-[#C6A87C] text-sm">ℹ</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-white/70">{infoText}</p>
                    <p className="text-xs text-white/35">
                      You can change these settings after joining
                    </p>
                  </div>
                </div>
              </div>

              <div
                className="rounded-2xl p-5 border border-white/[0.06]"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <p className="text-xs text-white/40 mb-3 font-medium uppercase tracking-widest">
                  Share this meeting
                </p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={
                      meeting?.shareableLink ||
                      `${window.location.origin}/user-meet/join/${joinCode}`
                    }
                    className="flex-1 bg-white/[0.04] border border-white/[0.1] rounded-lg px-4 py-3 text-white/60 text-xs font-mono truncate focus:outline-none"
                  />
                  <button
                    onClick={copyLink}
                    className="glass border border-white/10 rounded-lg px-4 hover:border-[#C6A87C]/50 transition-colors"
                    title="Copy link"
                  >
                    {copied ? (
                      <Check size={16} className="text-green-400" />
                    ) : (
                      <Copy size={16} className="text-white/50" />
                    )}
                  </button>
                </div>
              </div>

              {isFull && (
                <div className="rounded-2xl p-4 border border-orange-500/20 bg-orange-500/[0.06] flex items-center gap-3">
                  <AlertCircle size={16} className="text-orange-400 shrink-0" />
                  <span className="text-orange-400 text-sm">
                    This meeting is full ({capacity}/{capacity} participants).
                  </span>
                </div>
              )}

              {joinUiError && (
                <div className="rounded-2xl p-4 border border-red-500/20 bg-red-500/[0.06] flex items-center gap-3">
                  <AlertCircle size={16} className="text-red-400 shrink-0" />
                  <span className="text-red-400 text-sm">{joinUiError}</span>
                </div>
              )}

              <button
                type="button"
                onClick={handleJoinNow}
                disabled={joining || !canJoin}
                className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-2xl bg-[#C6A87C] text-[#061412] font-semibold hover:bg-[#D9BC93] transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
              >
                {joining ? (
                  <>
                    <RefreshCw
                      size={18}
                      className="animate-spin"
                      aria-hidden="true"
                    />
                    <span>Joining…</span>
                  </>
                ) : (
                  <>
                    <LogIn
                      size={18}
                      className="group-hover:translate-x-0.5 transition-transform"
                      aria-hidden="true"
                    />
                    <span>{isFull ? "Meeting Full" : "Join Now"}</span>
                    {!isFull && seatsLeft > 0 && (
                      <span className="ml-auto text-[#061412]/60 text-xs font-normal">
                        {seatsLeft} seat{seatsLeft !== 1 ? "s" : ""} left
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>

            {/* ── RIGHT: Table visual + participant list ── */}
            <div className="space-y-6">
              <div
                className="rounded-2xl p-8 border border-white/[0.06] flex flex-col items-center"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="flex items-center justify-between w-full mb-6">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-white/40">
                    {capacity > 10 ? "Auditorium view" : "Seating view"}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-[#C6A87C]/70 font-medium uppercase tracking-wider">
                      {roomInfo.label}
                    </span>
                    <span className="text-xs text-white/40">
                      {filled}/{capacity}
                    </span>
                  </div>
                </div>

                <BoardroomTable
                  seats={seatSlots}
                  capacity={capacity}
                  meetingTitle={meeting.title}
                />

                <div className="mt-6 flex items-center gap-4 text-xs text-white/50 self-start">
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full border-2 border-white/30 bg-[#C6A87C]/20 inline-block" />
                    Occupied
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full border-2 border-dashed border-white/20 inline-block" />
                    Open
                  </span>
                </div>
              </div>

              <div
                className="rounded-2xl border border-white/[0.06] p-6"
                style={{ background: "rgba(255,255,255,0.03)" }}
              >
                <div className="mb-4 flex items-center justify-between">
                  <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-white/40">
                    <Users size={12} aria-hidden="true" />
                    Participants
                  </p>
                  <span className="text-xs text-white/40">
                    {filled} / {capacity}
                  </span>
                </div>

                <div className="mb-5">
                  <div className="h-1.5 overflow-hidden rounded-full bg-white/[0.06]">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${Math.min(100, Math.round((filled / capacity) * 100))}%`,
                        background:
                          filled / capacity >= 0.9
                            ? "linear-gradient(90deg, #C6A87C, #D9BC93)"
                            : filled / capacity >= 0.6
                              ? "linear-gradient(90deg, #7BC8A6, #C6A87C)"
                              : "linear-gradient(90deg, #7BC8A6, #2A7E5D)",
                      }}
                    />
                  </div>
                </div>

                <div className="grid h-[380px] grid-cols-2 gap-2 overflow-y-auto pr-2 custom-scrollbar">
                  {visibleSeats.map((occupant, i) =>
                    occupant ? (
                      <ParticipantRow
                        key={occupant?.userId || occupant?._id || i}
                        occupant={occupant}
                        index={i}
                      />
                    ) : (
                      <EmptySeatRow key={`empty-${i}`} index={i} />
                    ),
                  )}
                  {capacity > 10 && seatsLeft > 5 && (
                    <div className="col-span-2 py-3 text-center">
                      <span className="text-xs italic text-white/25">
                        and {seatsLeft - 5} more open seat
                        {seatsLeft - 5 !== 1 ? "s" : ""}…
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
